import { IClearance } from "@/models/clearance.entity";
import { IContract } from "@/models/contract.entity";
import { CustomerContractRateBase } from "@/models/customer-contract-rate.entity";
import { IReport } from "@/models/report.entity";
import { VehicleContractBase } from "@/models/vehicle-contract-base.entity";
import { VehicleInsuranceContract, VehicleLeasingContract } from "@/models/vehicle-contract.entity";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { ContractTypeEnum } from "../enum/contract-type.enum";
import { CurrencyEnum } from "../enum/currency.enum";
import { VehicleContractTypeEnum } from "../enum/vehicle-contract-type.enum";
import { VehicleOwnershipStatusEnum } from "../enum/vehicle-ownership-status.enum";
import { $t } from "./t";
import { formatCurrency } from "./currency-helper";

/**
 * Utility class to determine approval limit and whether approval is required.
 */
export class ApprovalLimitChecker {
  private contract: IContract | VehicleContractBase;
  private report?: IReport;
  private clearance?: IClearance;

  private _limit?: number;
  private _limitSourceName?: string;

  constructor(contract: IContract | VehicleContractBase, report?: IReport, clearance?: IClearance) {
    this.contract = contract;
    this.report = report;
    this.clearance = clearance;
  }

  /**
   * Returns true if approval is required based on limit and clearance.
   */
  get isRequired(): boolean {
    const limit = this.limit;
    const total = this.clearance?.totalNet;
    return typeof limit === "number" && typeof total === "number" && limit <= total;
  }

  /**
   * Returns the approval limit, and tracks internally which field was used.
   */
  get limit(): number | undefined {
    if (this._limit !== undefined) return this._limit;

    const c = this.contract;
    const report = this.report;
    let currentMin: { key: string; value: number } | undefined;

    // === CUSTOMER CONTRACT ===
    if ("type" in c && c.type === ContractTypeEnum.CUSTOMER) {
      const details = c.details as CustomerContractRateBase;

      if (report?.vehicle?.ownership === VehicleOwnershipStatusEnum.OWNED) {
        const val = details.purchaseVehicleRepairApprovalLimit;
        if (typeof val === "number") {
          currentMin = { key: "objects.contract.customer.purchaseVehicleRepairApprovalLimit", value: val };
        }
      }

      if (
        report &&
        [CoverageTypeEnum.FULLY_COMPREHENSIVE_COVERAGE, CoverageTypeEnum.PARTIALLY_COMPREHENSIVE_COVERAGE].includes(
          report.coverageType as CoverageTypeEnum
        )
      ) {
        const val = details.comprehensiveRepairApprovalLimit;
        if (typeof val === "number" && (!currentMin || val < currentMin.value)) {
          currentMin = { key: "objects.contract.customer.comprehensiveRepairApprovalLimit", value: val };
        }
      }

      if (
        report &&
        [
          CoverageTypeEnum.BUSINESS_LIABILITY_COVERAGE,
          CoverageTypeEnum.MOTORISTS_THIRD_PARTY_LIABILITY_COVERAGE
        ].includes(report.coverageType as CoverageTypeEnum)
      ) {
        const val = details.liabilityRepairApprovalLimit;
        if (typeof val === "number" && (!currentMin || val < currentMin.value)) {
          currentMin = { key: "objects.contract.customer.liabilityRepairApprovalLimit", value: val };
        }
      }
    }

    // === VEHICLE CONTRACT ===
    else if ("contractType" in c) {
      switch (c.contractType) {
        case VehicleContractTypeEnum.INSURANCE: {
          const insurance = c as VehicleInsuranceContract;

          if (report?.coverageType === CoverageTypeEnum.FULLY_COMPREHENSIVE_COVERAGE) {
            const val = insurance.fullComprehensiveAppraiserLimit;
            if (typeof val === "number") {
              currentMin = { key: "objects.contract.insurance.fullComprehensiveAppraiserLimit", value: val };
            }
          }

          if (report?.coverageType === CoverageTypeEnum.PARTIALLY_COMPREHENSIVE_COVERAGE) {
            const val = insurance.partialCoverageAppraiserLimit;
            if (typeof val === "number" && (!currentMin || val < currentMin.value)) {
              currentMin = { key: "objects.contract.insurance.partialCoverageAppraiserLimit", value: val };
            }
          }
          break;
        }

        case VehicleContractTypeEnum.LEASING: {
          const leasing = c as VehicleLeasingContract;
          const val = leasing.approvalLimit;
          if (typeof val === "number") {
            currentMin = { key: "objects.contract.leasing.approvalLimit", value: val };
          }
          break;
        }
      }
    }

    if (currentMin) {
      this._limit = currentMin.value;
      this._limitSourceName = currentMin.key;
      return this._limit;
    }

    return undefined;
  }

  /**
   * Returns the raw property name that provided the limit (e.g. "approvalLimit").
   */
  get limitSourceName(): string | undefined {
    void this.limit;
    return this._limitSourceName;
  }

  /**
   * Returns the contract's currency or defaults to EUR.
   */
  get currency(): CurrencyEnum {
    let currency = CurrencyEnum.EUR;
    if ("currency" in this.contract && this.contract.currency) {
      currency = this.contract.currency;
    }
    return currency;
  }

  /**
   * Returns an explanatory string why approval is (not) required,
   * including the source field name that caused the decision.
   */
  explanation(): string {
    const limit = this.limit;
    const total = this.clearance?.totalNet;
    const source = this.limitSourceName;

    if (limit === undefined) {
      let coverageType = "";
      if (this.report?.coverageType) {
        coverageType = $t("enums.CoverageTypeEnum." + this.report?.coverageType);
      }

      return $t("clearanceApproval.noApprovalLimitDefined", { coverageType: coverageType });
    }

    if (total === undefined) {
      return $t("clearanceApproval.noClearanceAmountDefined");
    }

    const suffix = source ? $t("clearanceApproval.source", { source: $t(source) }) : "";

    if (this.isRequired) {
      return $t("clearanceApproval.clearanceRequired", {
        total: formatCurrency(total, this.currency),
        limit: formatCurrency(limit, this.currency),
        reason: suffix
      });
    }

    return $t("clearanceApproval.clearanceOptional", {
      total: formatCurrency(total, this.currency),
      limit: formatCurrency(limit, this.currency),
      reason: suffix
    });
  }
}
